import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';

import Modal from 'components/modal';
import useSearchRestaurants from 'pages/store-locator/hooks/use-search-restaurants';
import useStoreLocatorTabs from 'pages/store-locator/hooks/use-store-locator-tabs';
import { useSearching } from 'pages/store-locator/new-ui/hooks/use-searching';
import { useStoresReducer } from 'pages/store-locator/new-ui/hooks/use-stores-reducer';
import { SearchBar } from 'pages/store-locator/search-bar';
import { TabPanels, TabsBar } from 'pages/store-locator/tabs';
import { useGeolocation } from 'state/geolocation';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { isValidPosition } from 'utils/geolocation';

import { ModalBottom, ModalContent, ModalHeading, ModalTop } from '../store-selector.styled';
import { IStoreSelectorProps } from '../types';

import { StoreCardList } from './store-card-list';

export const StoreSelector: React.FC<IStoreSelectorProps> = ({ onDismiss, updateStore }) => {
  const {
    errorFavs,
    errorNearby,
    errorRecent,
    searchingNearby,
    searchingRecent,
    searchingFavs,
    storesNearby,
    storesFavs,
    storesRecent,
    storeLocatorDispatch,
  } = useStoresReducer();
  const {
    searchFavRestaurants,
    searchNearbyRestaurants,
    searchRecentRestaurants,
  } = useSearchRestaurants({
    storeLocatorDispatch,
  });
  const { isSearchingFavorites, isSearchingNearby, isSearchingRecent } = useSearching({
    searchingNearby,
    searchingRecent,
    searchingFavs,
  });
  const {
    currentTabHasError,
    currentTabIndex,
    handleTabChange,
    locatorTabs,
    locatorTabIds,
  } = useStoreLocatorTabs({ errorFavs, errorNearby, errorRecent });
  const {
    activeCoordinates,
    manualAddress,
    setManualAddress,
    isPermissionGranted,
    updateManualCoordinates,
  } = useGeolocation();
  const { formatMessage } = useIntl();

  const handleTabChangeEvent = useCallback(
    (_event: React.ChangeEvent, newValue: number) => {
      handleTabChange(newValue);
    },
    [handleTabChange]
  );

  const handleSelectStore = useCallback(
    (storeId: string) => {
      onDismiss();
      updateStore(storeId);
    },
    [onDismiss, updateStore]
  );

  const nearbyStoreCardsEl = (
    <StoreCardList
      currentTabHasError={currentTabHasError}
      isSearching={isSearchingNearby}
      onSelectStore={handleSelectStore}
      restaurants={storesNearby ?? []}
      tab={OrderLocatorTab.NEARBY}
    />
  );

  const favStoreCardsEl = (
    <StoreCardList
      currentTabHasError={currentTabHasError}
      isSearching={isSearchingFavorites}
      onSelectStore={handleSelectStore}
      restaurants={storesFavs ?? []}
      tab={OrderLocatorTab.FAVORITE}
    />
  );

  const recentStoreCardsEl = (
    <StoreCardList
      currentTabHasError={currentTabHasError}
      isSearching={isSearchingRecent}
      onSelectStore={handleSelectStore}
      restaurants={storesRecent ?? []}
      tab={OrderLocatorTab.RECENT}
    />
  );

  const cardsByTab = {
    [OrderLocatorTab.NEARBY]: nearbyStoreCardsEl,
    [OrderLocatorTab.FAVORITE]: favStoreCardsEl,
    [OrderLocatorTab.RECENT]: recentStoreCardsEl,
  };

  useEffect(() => {
    if (isValidPosition(activeCoordinates)) {
      searchNearbyRestaurants({
        location: activeCoordinates,
      });
    }
  }, [activeCoordinates, searchNearbyRestaurants]);

  useEffect(() => {
    searchFavRestaurants(activeCoordinates);
  }, [activeCoordinates, searchFavRestaurants]);

  useEffect(() => {
    searchRecentRestaurants(activeCoordinates);
  }, [activeCoordinates, searchRecentRestaurants]);

  return (
    <Modal onDismiss={onDismiss}>
      <ModalContent data-testid="support-form-v2-store-selector-refresh">
        <ModalTop>
          <ModalHeading>{formatMessage({ id: 'searchLocations' })}</ModalHeading>
          <SearchBar
            autoFocus={!manualAddress && isPermissionGranted}
            onAddressSelected={updateManualCoordinates}
            onInputValueChange={setManualAddress}
            value={manualAddress}
          />
          <TabsBar
            locatorTabs={locatorTabs}
            locatorTabIds={locatorTabIds}
            currentTabIndex={currentTabIndex}
            handleTabChangeEvent={handleTabChangeEvent}
            hideDeliveryTab={true}
          />
        </ModalTop>
        <ModalBottom>
          <TabPanels
            locatorTabs={locatorTabs}
            locatorTabIds={locatorTabIds}
            currentTabIndex={currentTabIndex}
            cardsByTab={cardsByTab}
          />
        </ModalBottom>
      </ModalContent>
    </Modal>
  );
};

export default StoreSelector;
